import {useState} from "react";
import Alert from "react-s-alert";
import {Admission, PatchAdmissionDto} from "@reside/reside-api-admission";

import {AdmissionsService} from "../../services/AdmissionsService";
import {useAdmissionsByStatusQueryUtils} from "../dashboard-table/hooks/useAdmissionsByStatusQuery";

/**
 * Resolves a PENDING admission's workflow type by PATCHing it, then updates the dashboard
 * cache in place so the card re-renders with its real workflow instead of the "pending" variant.
 * Callers whose own admission list isn't backed by that cache (e.g. admin admissions table,
 * resident admissions list) can pass `onSuccess` to refresh their own data instead.
 */
export const useSetAdmissionWorkflow = (
  onSuccess?: (admission: Admission) => void,
) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {updateAdmissionInCache} = useAdmissionsByStatusQueryUtils();

  const setWorkflow = async (
    admissionId: string,
    workflowType: PatchAdmissionDto.WorkflowTypeEnum,
  ): Promise<boolean> => {
    setIsSubmitting(true);

    try {
      const response = await AdmissionsService.patch(admissionId, {
        workflowType,
      });

      if (!response.ok || !response.data) {
        throw new Error("Failed to set workflow");
      }

      updateAdmissionInCache(response.data as Admission);
      onSuccess?.(response.data as Admission);

      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error({setWorkflowError: error});
      Alert.error("Failed to set workflow. Please, try again later.");

      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {isSubmitting, setWorkflow};
};
